import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import FacebookIcon from '@mui/icons-material/Facebook';
import ReactMarkdown from 'react-markdown';
import { TENANTS } from '../../tenants';
import API from '../../API';
import { selectVisibleNewArrivals } from '../../utils/newArrivals';
import { newsImageUrl } from '../../utils/newsImage';
import { newsEnabled } from '../../utils/site';

type FeatureCard = {
  title: string;
  hint: string;
  accent: string;
  /** Which pick from the slot's randomized pair to show. 0 = primary
   *  (always rendered), 1 = secondary (gated by `revealAt`). When the
   *  pool was too small for a distinct secondary, the card skips
   *  rendering entirely. */
  pick?: number;
  /** Minimum viewport width in pixels at which this card becomes
   *  visible. Omitted means "always visible." Used to stagger
   *  secondary cards in/out as the viewport widens or narrows,
   *  instead of jumping from 3 → 5 in one step. Pixel values rather
   *  than MUI breakpoint names because MUI's `lg` (1200) and `xl`
   *  (1536) leave too big a gap for the actual card geometry. */
  revealAt?: number;
};

type ShopFeature = {
  tenantId: string;
  heading: string;
  blurb: string;
  cards: FeatureCard[];
};

// Single highlighted item per tenant — shape used for both the
// "New arrivals" card (most-recently created) and the "Best sellers"
// card (top by sales volume). Populated on mount from separate
// endpoints; the card-render logic picks the right map by slot name.
type FeaturedItem = {
  itemId: string;
  name: string;
  imageId?: string;
  imageRoot?: string;
};

// How many items to pull per (tenant, slot) so the random picker has
// a meaningful pool. The server caps at 50 for /items/recent; 20 is
// plenty to keep selection lively across page loads.
const POOL_LIMIT = 20;

// How many distinct items each slot pre-selects from its pool. Sized
// to the most-cards-per-slot any shop uses: Grow Shop has 3 Best
// Sellers (primary + 2 secondaries), Back Room has 2 of each. Extra
// picks are harmless — unused cards just don't index into them.
const PICKS_PER_SLOT = 3;

// Pick up to `n` distinct items from a pool via a partial
// Fisher-Yates shuffle. Reshuffles on every page load so the Home
// cards surface something different each visit, without giving up
// the deterministic SQL ordering on the server.
const pickN = (pool: FeaturedItem[], n: number): FeaturedItem[] => {
  if (pool.length === 0) return [];
  const arr = pool.slice();
  const out: FeaturedItem[] = [];
  const limit = Math.min(n, arr.length);
  for (let i = 0; i < limit; i++) {
    const j = i + Math.floor(Math.random() * (arr.length - i));
    [arr[i], arr[j]] = [arr[j], arr[i]];
    out.push(arr[i]);
  }
  return out;
};

const NEWS_LIMIT = 3;
const MAX_BODY_LENGTH = 280;

// Treat each newline in the source as a paragraph break so plain-text
// content reads naturally. Authors can still use full markdown (lists,
// bold, links, etc.) on top of this.
const normalizeBody = (body: string): string => body.replace(/\n+/g, '\n\n');

// Word-boundary truncation. No trailing ellipsis — the "more..." CTA
// rendered after the body serves that role.
const truncateBody = (body: string, max: number): string => {
  if (body.length <= max) return body;
  const cut = body.substring(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return lastSpace > max * 0.7 ? cut.substring(0, lastSpace) : cut;
};

const formatPostDate = (raw: string): string => {
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const shopFeatures: ShopFeature[] = [
  {
    tenantId: 'backroom',
    heading: 'The Back Room',
    blurb: 'Cannabis accessories — papers, glass, grinders, storage and more.',
    cards: [
      { title: 'Premium Rolling Papers', hint: 'Featured product', accent: '#1f2937' },
      { title: 'Glass Pipes Collection', hint: 'New arrivals', accent: '#334155' },
      { title: 'Storage & Stash',        hint: 'Hot sellers',  accent: '#475569' },
      // Secondary picks — different random items from the same
      // pools. Staggered reveal so the row grows 3 → 4 → 5 as the
      // viewport widens. Thresholds picked from the actual layout
      // math: 240px cards + 24px gap + Container padding.
      // 4 cards need ≈1080px viewport, 5 need ≈1344px.
      { title: 'Glass Pipes Collection', hint: 'New arrivals', accent: '#334155', pick: 1, revealAt: 1100 },
      { title: 'Storage & Stash',        hint: 'Hot sellers',  accent: '#475569', pick: 1, revealAt: 1360 },
    ],
  },
  {
    tenantId: 'atlanticgrowshop',
    heading: 'The Grow Shop',
    blurb: 'Everything you need to grow at home — lights, nutrients, climate control.',
    cards: [
      { title: 'LED Grow Lights',         hint: 'Featured product', accent: '#14532d' },
      // Second featured slot — the next-most-recently-flagged item.
      // Always visible (no `revealAt`), since the row has plenty of
      // room with the rest staggered.
      { title: 'Hydroponic Nutrients',    hint: 'Featured product', accent: '#166534', pick: 1 },
      { title: 'Climate & Ventilation',   hint: 'Best sellers',     accent: '#15803d' },
      // Two more Best Sellers picked from the same pool. Staggered
      // reveal so the row grows 3 → 4 → 5 with viewport width, same
      // pattern as Back Room's secondary New Arrivals / Hot Sellers.
      { title: 'Climate & Ventilation',   hint: 'Best sellers',     accent: '#15803d', pick: 1, revealAt: 1100 },
      { title: 'Climate & Ventilation',   hint: 'Best sellers',     accent: '#15803d', pick: 2, revealAt: 1360 },
    ],
  },
];

const tenantPathById = (id: string) =>
  TENANTS.find((t) => t.id === id)?.path ?? '/';

const Home = () => {
  // Captured so highlight-card clicks can pass `state.background =
  // location`, keeping Home mounted (and its scroll position
  // preserved) under the item-details dialog overlay. See `RoutedBody`
  // in App.tsx for how the background pattern is wired.
  const location = useLocation();
  const [posts, setPosts] = useState<INewsItem[]>([]);
  // Each record maps tenant id → up to two picks (primary, secondary).
  // The card-render logic indexes by `card.pick ?? 0`.
  const [newArrivals, setNewArrivals] = useState<Record<string, FeaturedItem[]>>({});
  const [bestSellers, setBestSellers] = useState<Record<string, FeaturedItem[]>>({});
  const [hotSellers, setHotSellers] = useState<Record<string, FeaturedItem[]>>({});
  const [featured, setFeatured] = useState<Record<string, FeaturedItem[]>>({});

  useEffect(() => {
    // News is disabled on production — skip the fetch entirely there.
    if (!newsEnabled()) return;
    let cancelled = false;
    (async () => {
      try {
        // News is global and the server returns newest-first, so we
        // can ask for just the top N rather than the full archive.
        const data = await API.getNews(NEWS_LIMIT, 0);
        if (!cancelled) {
          setPosts(data.items);
        }
      } catch (err) {
        // Leave posts empty; the section renders a "nothing yet" line.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        shopFeatures.map(async (shop) => {
          // Fetch enough to cover the New Arrivals page rule (≥6 or
          // all items within a week of the newest), then narrow to
          // exactly that set BEFORE picking. The Home highlights
          // should never feature an item that wouldn't appear on the
          // /new-arrivals page itself.
          const { items, imageRoot } = await API.getRecentItems(shop.tenantId, POOL_LIMIT);
          const visible = selectVisibleNewArrivals(items);
          const pool: FeaturedItem[] = visible.map((item) => ({
            itemId: item.id,
            name: item.name,
            imageId: item.imageId,
            imageRoot,
          }));
          return { tenantId: shop.tenantId, picks: pickN(pool, PICKS_PER_SLOT) };
        }),
      );
      if (cancelled) return;
      const next: Record<string, FeaturedItem[]> = {};
      for (const r of results) next[r.tenantId] = r.picks;
      setNewArrivals(next);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        shopFeatures.map(async (shop) => {
          const { items, imageRoot } = await API.getBestSellers(shop.tenantId, POOL_LIMIT);
          const pool: FeaturedItem[] = items.map((item) => ({
            itemId: item.id,
            name: item.name,
            imageId: item.imageId,
            imageRoot,
          }));
          return { tenantId: shop.tenantId, picks: pickN(pool, PICKS_PER_SLOT) };
        }),
      );
      if (cancelled) return;
      const next: Record<string, FeaturedItem[]> = {};
      for (const r of results) next[r.tenantId] = r.picks;
      setBestSellers(next);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        shopFeatures.map(async (shop) => {
          const { items, imageRoot } = await API.getHotSellers(shop.tenantId, POOL_LIMIT);
          const pool: FeaturedItem[] = items.map((item) => ({
            itemId: item.id,
            name: item.name,
            imageId: item.imageId,
            imageRoot,
          }));
          return { tenantId: shop.tenantId, picks: pickN(pool, PICKS_PER_SLOT) };
        }),
      );
      if (cancelled) return;
      const next: Record<string, FeaturedItem[]> = {};
      for (const r of results) next[r.tenantId] = r.picks;
      setHotSellers(next);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        shopFeatures.map(async (shop) => {
          const { items, imageRoot } = await API.getFeaturedItems(shop.tenantId, POOL_LIMIT);
          const pool: FeaturedItem[] = items.map((item) => ({
            itemId: item.id,
            name: item.name,
            imageId: item.imageId,
            imageRoot,
          }));
          // Featured is curatorial — the admin's most-recent toggle is
          // *the* signal — so honor server ordering (featured_at DESC)
          // instead of shuffling. `pick: 0` is the most-recently
          // featured item, `pick: 1` the second-most, and so on.
          return { tenantId: shop.tenantId, picks: pool.slice(0, PICKS_PER_SLOT) };
        }),
      );
      if (cancelled) return;
      const next: Record<string, FeaturedItem[]> = {};
      for (const r of results) next[r.tenantId] = r.picks;
      setFeatured(next);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box sx={{ backgroundColor: '#fafafa' }}>
      {/* Hero */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1f2937 60%, #14532d 100%)',
          color: '#f8fafc',
          py: { xs: 8, md: 14 },
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="overline"
            sx={{ letterSpacing: '0.2em', opacity: 0.7 }}
          >
            Seed to session.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mt: 2,
              mb: 3,
              lineHeight: 1.1,
              fontSize: { xs: '2rem', sm: '2.75rem', md: '3.75rem' },
            }}
          >
            The Back Room<br />and Grow Shop
          </Typography>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.85,
              mb: 5,
              maxWidth: 640,
              fontWeight: 400,
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}
          >
            Grow it in The Grow Shop. Enjoy it from The Back Room. Two shops,
            one cart, one checkout.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              component={RouterLink}
              to="/r/backroom"
              variant="contained"
              size="large"
              sx={{ backgroundColor: '#f8fafc', color: '#0f172a', '&:hover': { backgroundColor: '#e2e8f0' } }}
            >
              Shop The Back Room
            </Button>
            <Button
              component={RouterLink}
              to="/r/atlanticgrowshop"
              variant="contained"
              size="large"
              sx={{ backgroundColor: '#f8fafc', color: '#0f172a', '&:hover': { backgroundColor: '#e2e8f0' } }}
            >
              Shop The Grow Shop
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Featured from each shop */}
      <Container id="highlights" maxWidth="xl" sx={{ py: { xs: 8, md: 12 }, scrollMarginTop: '96px' }}>
        <Typography
          variant="overline"
          sx={{ letterSpacing: '0.2em', color: '#64748b' }}
        >
          Featured
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, mb: 6, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          Highlights from each shop
        </Typography>

        {shopFeatures.map((shop) => (
          <Box key={shop.tenantId} sx={{ mb: 8, '&:last-child': { mb: 0 } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                mb: 3,
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {shop.heading}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                  {shop.blurb}
                </Typography>
              </Box>
              <Button
                component={RouterLink}
                to={tenantPathById(shop.tenantId)}
                size="small"
              >
                Browse →
              </Button>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 3,
                justifyContent: { xs: 'center', sm: 'flex-start' },
              }}
            >
              {shop.cards.map((card, idx) => {
                // Two slots are data-driven today; the "Featured
                // product" slot stays a static placeholder until we
                // add an editorial pick mechanism. Match by hint text
                // so re-ordering the cards doesn't silently break
                // the swap. Each slot also resolves its own list-page
                // link target.
                const slot: 'new-arrivals' | 'best-sellers' | 'hot-sellers' | 'featured' | null =
                  /new arrivals/i.test(card.hint)
                    ? 'new-arrivals'
                    : /best sellers/i.test(card.hint)
                    ? 'best-sellers'
                    : /hot sellers/i.test(card.hint)
                    ? 'hot-sellers'
                    : /featured/i.test(card.hint)
                    ? 'featured'
                    : null;
                const picks =
                  slot === 'new-arrivals'
                    ? newArrivals[shop.tenantId]
                    : slot === 'best-sellers'
                    ? bestSellers[shop.tenantId]
                    : slot === 'hot-sellers'
                    ? hotSellers[shop.tenantId]
                    : slot === 'featured'
                    ? featured[shop.tenantId]
                    : undefined;
                const arrival = picks?.[card.pick ?? 0];
                // Secondary cards depend on a distinct second pick
                // existing in the pool; when the pool was too small
                // (one item, or the slot fetch hadn't returned yet),
                // skip rendering rather than drop a stale placeholder.
                if (card.revealAt && !arrival) return null;
                const tenantPath = tenantPathById(shop.tenantId);
                // Featured cards go straight to the item detail (one
                // editorial pick at a time, so a list page would feel
                // empty). Other slots land on their respective list
                // pages.
                const linkTo = arrival && slot
                  ? slot === 'featured'
                    ? `${tenantPath}/item/${arrival.itemId}`
                    : `${tenantPath}/${slot}`
                  : undefined;
                const imageSrc =
                  arrival?.imageId && arrival?.imageRoot
                    ? `https://thequeensflorist.sirv.com/${arrival.imageRoot}/${arrival.imageId}.jpg?w=600`
                    : undefined;

                const cardBody = (
                  <Box
                    sx={{
                      // Match the item-card sizing used in the shop grid
                      // (Item.tsx renders ItemImage with height={180}).
                      // Fixed width here makes the highlight row read
                      // like a normal row of items rather than three
                      // billboards.
                      width: 240,
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 2,
                      overflow: 'hidden',
                      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                      },
                    }}
                  >
                    {imageSrc ? (
                      <Box
                        component="img"
                        src={imageSrc}
                        alt={arrival?.name ?? card.title}
                        sx={{
                          display: 'block',
                          width: '100%',
                          height: 180,
                          // `contain` keeps the full image visible
                          // (letterbox / pillarbox via the background
                          // colour) — matches how the item-grid
                          // `<ItemImage>` renders. `cover` was
                          // cropping the edges off taller items.
                          objectFit: 'contain',
                          objectPosition: 'center',
                          // Pillarbox / letterbox space stays the
                          // same white as the rest of the card so
                          // there's no visible grey strip.
                          backgroundColor: '#fff',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: 180,
                          background: card.accent,
                          color: 'rgba(255,255,255,0.4)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          letterSpacing: '0.15em',
                          textTransform: 'uppercase',
                        }}
                      >
                        Image placeholder
                      </Box>
                    )}
                    <Box sx={{ p: 2.5 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                        {card.hint}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 0.5 }}>
                        {arrival?.name ?? card.title}
                      </Typography>
                      {arrival && (
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              color: '#0f172a',
                              letterSpacing: '0.05em',
                            }}
                          >
                            more...
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );

                // Wrap in a responsive Box so reveal-gated cards
                // collapse entirely (no reserved space) below their
                // threshold. The flex parent then naturally re-flows
                // the remaining cards as the screen shrinks. Using a
                // raw `@media (min-width)` query instead of MUI's
                // named breakpoints so we can pick pixel thresholds
                // that line up with the actual card geometry.
                const displaySx =
                  card.revealAt !== undefined
                    ? {
                        display: 'none',
                        [`@media (min-width: ${card.revealAt}px)`]: { display: 'block' },
                      }
                    : undefined;
                if (linkTo) {
                  // Featured cards link to an item-detail URL and want
                  // the dialog overlay (`state.background = location`
                  // keeps Home mounted). New / Best / Hot Sellers cards
                  // link to actual list pages — they should navigate
                  // normally without a background, otherwise the main
                  // Switch would keep rendering Home and the list page
                  // would never appear.
                  const linkDest =
                    slot === 'featured'
                      ? { pathname: linkTo, state: { background: location } }
                      : linkTo;
                  return (
                    <Box key={idx} sx={displaySx}>
                      <RouterLink
                        to={linkDest}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        {cardBody}
                      </RouterLink>
                    </Box>
                  );
                }
                return (
                  <Box key={idx} sx={displaySx}>
                    {cardBody}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Container>

      {/* News / posts (hidden on production — see utils/site) */}
      {newsEnabled() && (
      <Box id="news" sx={{ borderTop: '1px solid #e5e7eb', backgroundColor: '#fff', scrollMarginTop: '96px' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <Typography variant="overline" sx={{ letterSpacing: '0.2em', color: '#64748b' }}>
            News
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, mb: 6, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
            From the shop
          </Typography>

          {posts.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              Nothing to share just yet — check back soon.
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 4,
              }}
            >
              {posts.map((post) => {
                const firstImage = post.images?.[0];
                const needsMore = post.body.length > MAX_BODY_LENGTH;
                const visibleBody = needsMore ? truncateBody(post.body, MAX_BODY_LENGTH) : post.body;
                return (
                  <Box key={post.id} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1 }}>
                      {formatPostDate(post.date)}
                    </Typography>
                    {firstImage && (
                      <Box
                        component="img"
                        src={newsImageUrl(firstImage)}
                        alt=""
                        sx={{
                          width: '100%',
                          aspectRatio: '16 / 9',
                          objectFit: 'cover',
                          objectPosition: 'center',
                          border: '1px solid #e5e7eb',
                          borderRadius: 2,
                          mb: 2,
                          backgroundColor: '#f1f5f9',
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        color: '#475569',
                        fontSize: '0.875rem',
                        lineHeight: 1.7,
                        '& p': { m: 0, mb: 0.5, '&:last-child': { mb: 0 } },
                        '& ul, & ol': { my: 0.5, pl: 2.5 },
                        '& li': { mb: 0.25 },
                        '& strong': { fontWeight: 700 },
                        '& em': { fontStyle: 'italic' },
                        '& a': { color: 'inherit', textDecoration: 'underline' },
                        '& h1, & h2, & h3, & h4': { fontWeight: 700, fontSize: '0.95rem', m: 0, mb: 0.5 },
                        '& code': { backgroundColor: '#f1f5f9', px: 0.5, borderRadius: 0.5, fontSize: '0.85em' },
                      }}
                    >
                      <ReactMarkdown>{normalizeBody(visibleBody)}</ReactMarkdown>
                    </Box>
                    {needsMore && (
                      <Box sx={{ mt: 0.5 }}>
                        <RouterLink
                          to={`/news#${post.id}`}
                          style={{
                            fontWeight: 700,
                            fontSize: '0.875rem',
                            color: '#0f172a',
                            textDecoration: 'none',
                          }}
                        >
                          more...
                        </RouterLink>
                      </Box>
                    )}
                    {post.facebook && (
                      <Box sx={{ mt: 'auto', pt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton
                          component="a"
                          href={post.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          aria-label="View on Facebook"
                          sx={{ color: '#94a3b8', '&:hover': { color: '#1877f2' } }}
                        >
                          <FacebookIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
          {posts.length > 0 && (
            <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
              <Button
                component={RouterLink}
                to="/news"
                variant="outlined"
                size="medium"
                sx={{
                  borderColor: '#cbd5e1',
                  color: '#0f172a',
                  letterSpacing: '0.05em',
                  '&:hover': { borderColor: '#0f172a', backgroundColor: 'transparent' },
                }}
              >
                See all posts →
              </Button>
            </Box>
          )}
        </Container>
      </Box>
      )}
    </Box>
  );
};

export default Home;
